// Which unit a slot's instruction belongs to. The slot name says almost all of it
// (the trace's own classification, which needs no spec); the shared `vex` slot is
// refined by mnemonic, a small heuristic on the text rather than an ISA table.
const GUARD = /^\((p\d+!?)\)\s*/;
const PREFIX = /^(valu|vex|vres)\d+$/;
const cache = new Map();

export function slotUnit(slotName, text) {
  const key = slotName + " " + text;
  const hit0 = cache.get(key);
  if (hit0) return hit0;
  const t = text.replace(GUARD, "").trim();
  let toks = t.split(/\s+/).filter(Boolean);
  if (PREFIX.test(toks[0] || "")) toks = toks.slice(1);
  const raw = toks[0] || "";
  let unit = unitOfSlot(slotName);
  if (unit === "vex") {
    if (/matmul|mult|mrb|\bmxu/.test(raw)) unit = "mxu";
    else if (/xpose|xlane|permute|rotate|reduce|xlu/.test(raw)) unit = "xlu";
    else if (/eup|rcp|rsqrt|\bexp|\blog|tanh|pow/.test(raw)) unit = "eup";
    else if (/prng|rand/.test(raw)) unit = "prng";
  }
  const out = { unit, mnemonic: raw, raw };
  cache.set(key, out);
  return out;
}

// A sync verb's operand: a bare number is one of this core's 512 flags; a routed
// address keeps the flag in the low 14 bits.
const FLAG_OPERAND = /\bflag=(0x[0-9a-fA-F]+|\d+)\b/;
export function decodeFlag(text) {
  const m = FLAG_OPERAND.exec(text);
  if (!m) return null;
  const raw = m[1].slice(0, 2) === "0x" ? parseInt(m[1], 16) : +m[1];
  return { raw, idx: raw & 0x3fff, sel: raw >> 14, at: m.index, len: m[0].length };
}

// The unit a slot name stands for, from its prefix: the bundle's own classification,
// which needs no spec. `vex` is the shared MXU/XLU slot; unit events refine it.
export const UNITS = ["scalar", "valu", "vex", "mxu", "xlu", "eup", "prng", "vres", "vld", "vst", "dma", "sync", "vmsk", "misc", "alu", "stream"];
export const UNIT_BIT = Object.fromEntries(UNITS.map((u, i) => [u, 1 << i]));
// Which unit a bundle is coloured by when several are populated.
export const DOMINANT = ["mxu", "xlu", "eup", "prng", "dma", "sync", "vst", "vld", "vres", "valu", "vex", "scalar", "stream", "alu", "vmsk", "misc"];
export const DOMINANT_BIT = DOMINANT.map((u) => UNIT_BIT[u]);

export function unitOfSlot(name) {
  if (/^s(\d|$)/.test(name)) return "scalar";   // a slot "s0", or the unit "s" a trace names
  for (const u of ["valu", "vex", "vres", "vld", "vst", "dma", "sync", "vmsk", "misc", "alu", "stream"]) {
    if (name.startsWith(u)) return u;
  }
  return "misc";
}

export function dominantUnit(mask) {
  for (let i = 0; i < DOMINANT.length; i++) if (mask & DOMINANT_BIT[i]) return DOMINANT[i];
  return null;
}

export function unitsOf(mask) {
  const out = [];
  for (const u of DOMINANT) if (mask & UNIT_BIT[u]) out.push(u);
  return out;
}

export const UNIT_COLOR = {
  scalar: "var(--u-scalar)", valu: "var(--u-valu)", mxu: "var(--u-mxu)", xlu: "var(--u-xlu)",
  vres: "var(--u-vres)", vld: "var(--u-vld)", vst: "var(--u-vst)", dma: "var(--u-dma)",
  sync: "var(--u-sync)", vmsk: "var(--u-vmsk)", eup: "var(--u-eup)", prng: "var(--u-prng)",
  vex: "var(--u-vex)", misc: "var(--u-misc)", alu: "var(--u-alu)", stream: "var(--u-stream)",
};
export const unitColor = (u) => UNIT_COLOR[u] || "var(--dim)";

// What each unit is, in a reader's order (the vector units together), for the guide
// and the slice map's key. Not DOMINANT's order: that one is the painter's precedence.
export const UNIT_ABOUT = [
  ["mxu", "matrix unit: the systolic array, one matmul pass per issue"],
  ["xlu", "cross-lane unit: transposes, permutes, reductions across lanes"],
  ["eup", "extended unary: exp, log, rsqrt, tanh and the rest"],
  ["prng", "random numbers"],
  ["valu", "vector ALU: adds, multiplies, selects, compares"],
  ["vex", "the vector extended slot the matrix, cross-lane and unary units share"],
  ["vres", "results popped back from those units"],
  ["vld", "vector loads from memory"],
  ["vst", "vector stores to memory"],
  ["vmsk", "vector masks and predicates"],
  ["scalar", "scalar unit: addresses, counters, branches"],
  ["sync", "sync flags: set, wait, barrier"],
  ["dma", "DMA descriptors this core pushed"],
  ["stream", "SparseCore streams: gathers and scatters through memory"],
  ["alu", "SparseCore scalar ALU"],
  ["misc", "everything else a bundle can carry"],
];

// Canvas cannot take `var()`: the computed values, read once per theme.
let rgb = null;
const EXTRA = ["wait", "nop", "idle", "host", "sc", "arc", "fg", "dim", "dimmer", "line", "panel", "bg", "accent", "hi", "cross", "sel"];
export function unitRGB(u) {
  if (!rgb) {
    rgb = {};
    const cs = getComputedStyle(document.documentElement);
    for (const k of UNITS) rgb[k] = cs.getPropertyValue("--u-" + k).trim() || "#888";
    for (const k of EXTRA) rgb[k] = cs.getPropertyValue("--" + k).trim() || "#888";
  }
  return rgb[u] || rgb.dim;
}
export function resetRGB() { rgb = null; }
